import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Home, FileText, Palette, Kanban, Upload, Plus, Check, X, Download, Settings as SettingsIcon, KeyRound, Receipt } from "lucide-react";
import api, { FILES_BASE } from "@/lib/api";
import { formatApiError } from "@/lib/auth";
import { toast } from "sonner";

export default function ClientPortal() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("home");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/portal/me");
        setData(data);
      } catch {
        nav("/login");
      } finally {
        setLoading(false);
      }
    })();
  }, [nav]);

  const logout = async () => {
    await api.post("/auth/logout");
    nav("/");
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading…</div>;
  if (!data) return null;

  const color = data.agency.branding?.primary_color || "#0F172A";
  const tenantStyles = { "--tenant-primary": color, "--tenant-primary-hover": color };

  const tabs = [
    { key: "home", label: "Home", icon: Home },
    { key: "contracts", label: "Contracts", icon: FileText },
    { key: "invoices", label: "Invoices", icon: Receipt },
    { key: "brand", label: "Brand Assets", icon: Palette },
    { key: "roadmap", label: "Roadmap", icon: Kanban },
    { key: "settings", label: "Settings", icon: SettingsIcon },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAFA]" style={tenantStyles} data-testid="client-portal">
      <nav className="glass sticky top-0 z-40 px-6 py-4 flex items-center justify-between border-b border-slate-200/50">
        <div className="flex items-center gap-3">
          {data.agency.branding?.logo_url ? (
            <img src={data.agency.branding.logo_url} alt={data.agency.name} className="h-8" />
          ) : (
            <div className="w-8 h-8 rounded-lg text-white flex items-center justify-center font-bold" style={{ backgroundColor: color }}>{data.agency.name[0]}</div>
          )}
          <span className="font-bold tracking-tight" data-testid="portal-agency-name">{data.agency.name}</span>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-slate-600 hidden md:inline" data-testid="portal-client-name">{data.client.name}</span>
          <button onClick={logout} className="text-sm text-slate-600 hover:text-slate-900 inline-flex items-center gap-1 px-3 py-1.5 rounded-full hover:bg-slate-100" data-testid="portal-logout">
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </div>
      </nav>

      <div className="px-4 md:px-10 pt-8 pb-6 max-w-6xl mx-auto">
        <p className="eyebrow mb-2">Tenant portal</p>
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-slate-900" data-testid="portal-welcome">Welcome, {data.client.name.split(" ")[0]}.</h1>
      </div>

      <div className="px-4 md:px-10 max-w-6xl mx-auto">
        <div className="flex gap-1 border-b border-slate-200 mb-8 overflow-x-auto" data-testid="portal-tabs">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 ${tab === t.key ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-900"}`}
              data-testid={`portal-tab-${t.key}`}
            >
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        <div className="pb-20">
          {tab === "home" && <PortalHome agency={data.agency} client={data.client} color={color} />}
          {tab === "contracts" && <PortalContracts color={color} />}
          {tab === "invoices" && <PortalInvoices color={color} />}
          {tab === "brand" && <PortalBrand color={color} />}
          {tab === "roadmap" && <PortalRoadmap color={color} clientId={data.client.id} />}
          {tab === "settings" && <PortalSettings color={color} client={data.client} />}
        </div>
      </div>
      <footer className="px-4 md:px-10 max-w-6xl mx-auto pb-10 text-center">
        <a
          href="https://foliosy.com"
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-slate-400 hover:text-slate-700"
          data-testid="portal-foliosy-credit"
        >
          Powered by Foliosy — run your own agency →
        </a>
      </footer>
    </div>
  );
}

function PortalInvoices({ color }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/portal/invoices");
        setInvoices(data);
      } catch (err) {
        toast.error(formatApiError(err));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const download = async (id) => {
    try {
      const { data } = await api.get(`/portal/invoices/${id}/pdf-url`);
      window.open(`${FILES_BASE}${data.url}`, "_blank", "noopener");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const statusStyle = (s) => {
    if (s === "Paid") return "bg-emerald-50 text-emerald-700";
    if (s === "Overdue") return "bg-red-50 text-red-700";
    if (s === "Cancelled") return "bg-slate-100 text-slate-400 line-through";
    return "bg-blue-50 text-blue-700";
  };

  const sym = (c) => ({ EUR: "€", USD: "$", GBP: "£" }[c] || "");

  if (loading) return <div className="text-slate-400">Loading invoices…</div>;

  if (invoices.length === 0) {
    return (
      <div className="bento p-10 text-center" data-testid="portal-invoices-empty">
        <p className="eyebrow mb-2">Invoices</p>
        <h3 className="text-xl font-bold tracking-tight mb-2">Nothing here yet.</h3>
        <p className="text-slate-500 text-sm">Your agency will send invoices here when they&apos;re ready.</p>
      </div>
    );
  }

  return (
    <div data-testid="portal-invoices">
      <p className="eyebrow mb-2">Billing</p>
      <h2 className="text-3xl font-bold tracking-tight mb-6">Your invoices</h2>
      <div className="bento overflow-hidden">
        <table className="w-full text-left">
          <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
            <tr>
              <th className="px-6 py-4">Number</th>
              <th className="px-6 py-4">Issued</th>
              <th className="px-6 py-4">Due</th>
              <th className="px-6 py-4 text-right">Total</th>
              <th className="px-6 py-4">Status</th>
              <th className="px-6 py-4 text-right"> </th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((inv) => (
              <tr key={inv.id} className="border-t border-slate-100" data-testid={`portal-invoice-row-${inv.id}`}>
                <td className="px-6 py-4 font-mono text-xs font-semibold text-slate-900">{inv.invoice_number}</td>
                <td className="px-6 py-4 text-xs text-slate-500 font-mono">{inv.issue_date}</td>
                <td className="px-6 py-4 text-xs text-slate-500 font-mono">{inv.due_date}</td>
                <td className="px-6 py-4 text-right font-medium text-slate-900">{sym(inv.currency)}{Number(inv.total || 0).toFixed(2)}</td>
                <td className="px-6 py-4">
                  <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${statusStyle(inv.status)}`}>{inv.status}</span>
                </td>
                <td className="px-6 py-4 text-right">
                  <button
                    onClick={() => download(inv.id)}
                    className="inline-flex items-center gap-1 text-xs text-white px-3 py-1.5 rounded-full active:scale-95"
                    style={{ backgroundColor: color }}
                    data-testid={`portal-invoice-download-${inv.id}`}
                  >
                    <Download className="w-3.5 h-3.5" /> PDF
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PortalSettings({ color, client }) {  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) {
      toast.error("New passwords don't match");
      return;
    }
    if (next.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/portal/change-password", { current_password: current, new_password: next });
      toast.success("Password updated");
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="portal-settings">
      <div className="bento p-8">
        <p className="eyebrow mb-2">Account</p>
        <h2 className="text-2xl font-bold tracking-tight mb-1">Profile</h2>
        <p className="text-slate-500 text-sm mb-6">These details are managed by your agency.</p>
        <div className="grid sm:grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-slate-500 mb-1">Name</p>
            <p className="font-medium text-slate-900" data-testid="settings-name">{client.name}</p>
          </div>
          <div>
            <p className="text-slate-500 mb-1">Email</p>
            <p className="font-medium text-slate-900" data-testid="settings-email">{client.email}</p>
          </div>
        </div>
      </div>

      <div className="bento p-8">
        <p className="eyebrow mb-2">Security</p>
        <h2 className="text-2xl font-bold tracking-tight mb-1">Change password</h2>
        <p className="text-slate-500 text-sm mb-6">Use a strong, unique password. We recommend at least 8 characters.</p>
        <form onSubmit={submit} className="space-y-4 max-w-md" data-testid="change-password-form">
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Current password</label>
            <input
              required
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:bg-white focus:ring-2 focus:ring-slate-900/5 focus:border-slate-900 outline-none"
              data-testid="current-password"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">New password</label>
            <input
              required
              type="password"
              minLength={6}
              value={next}
              onChange={(e) => setNext(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:bg-white focus:ring-2 focus:ring-slate-900/5 focus:border-slate-900 outline-none"
              data-testid="new-password"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Confirm new password</label>
            <input
              required
              type="password"
              minLength={6}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:bg-white focus:ring-2 focus:ring-slate-900/5 focus:border-slate-900 outline-none"
              data-testid="confirm-password"
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="text-white rounded-full px-6 py-3 font-medium hover:opacity-90 active:scale-95 disabled:opacity-50 inline-flex items-center gap-2"
            style={{ backgroundColor: color }}
            data-testid="change-password-submit"
          >
            <KeyRound className="w-4 h-4" />
            {submitting ? "Updating…" : "Update password"}
          </button>
        </form>
      </div>
    </div>
  );
}

const PROGRESS_BY_STATUS = { Completed: 100, InProgress: 50, NotStarted: 0 };

function PortalHome({ agency, client, color }) {
  const checklist = agency.branding?.welcome_checklist || [];
  const progress = PROGRESS_BY_STATUS[client.onboarding_status] ?? 0;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 animate-fade-up" data-testid="portal-home">
      <div className="bento p-8 lg:col-span-2" data-testid="welcome-checklist-card">
        <p className="eyebrow mb-3">Welcome checklist</p>
        <h2 className="text-2xl font-bold mb-6">Get set up with {agency.name}</h2>
        <ul className="space-y-3">
          {checklist.length === 0 && <li className="text-slate-500 text-sm">Your agency hasn't configured a checklist yet.</li>}
          {checklist.map((item, i) => (
            <li key={`${item}-${i}`} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
              <div className="w-6 h-6 rounded-full border-2 flex items-center justify-center" style={{ borderColor: color }}>
                <span className="text-xs" style={{ color }}>{i + 1}</span>
              </div>
              <span className="text-sm text-slate-800 font-medium">{item}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="bento p-8 grain-overlay text-white" style={{ backgroundColor: color }} data-testid="progress-card">
        <p className="eyebrow text-white/60 mb-3">Progress</p>
        <div className="relative w-40 h-40 mx-auto my-6">
          <svg className="w-full h-full -rotate-90">
            <circle cx="80" cy="80" r="70" stroke="rgba(255,255,255,0.2)" strokeWidth="10" fill="none" />
            <circle cx="80" cy="80" r="70" stroke="#ffffff" strokeWidth="10" fill="none" strokeDasharray={2 * Math.PI * 70} strokeDashoffset={(2 * Math.PI * 70) * (1 - progress / 100)} strokeLinecap="round" style={{ transition: "stroke-dashoffset 0.8s" }} />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center text-4xl font-bold">{progress}%</div>
        </div>
        <p className="text-center text-sm opacity-80">{client.onboarding_status.replace(/([A-Z])/g, " $1").trim()}</p>
      </div>
    </div>
  );
}

function PortalContracts({ color }) {
  const [contracts, setContracts] = useState([]);
  const [signing, setSigning] = useState(null);
  const [signature, setSignature] = useState("");
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  const load = async () => {
    const { data } = await api.get("/portal/contracts");
    setContracts(data);
  };

  useEffect(() => { load(); }, []);

  const sign = async () => {
    try {
      await api.post(`/portal/contracts/${signing.id}/sign`, { signature_name: signature });
      toast.success("Contract signed! PDF generated.");
      setSigning(null);
      setSignature("");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const viewPdf = async (id) => {
    const { data } = await api.get(`/portal/contracts/${id}/pdf-url`);
    window.open(BACKEND + data.url, "_blank");
  };

  return (
    <div className="space-y-4 animate-fade-up" data-testid="portal-contracts">
      {contracts.length === 0 && <div className="bento p-12 text-center text-slate-500">No contracts yet.</div>}
      {contracts.map((c) => (
        <div key={c.id} className="bento p-6" data-testid={`portal-contract-${c.id}`}>
          <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
            <div>
              <h3 className="text-xl font-bold text-slate-900">{c.title}</h3>
              <p className="text-sm text-slate-500 font-mono mt-1">${c.price.toFixed(2)}</p>
            </div>
            <span className={`text-xs font-medium px-3 py-1 rounded-full ${c.status === "Signed" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{c.status}</span>
          </div>
          <p className="text-sm text-slate-600 whitespace-pre-wrap mb-4">{c.scope_details}</p>
          <div className="flex gap-2 flex-wrap">
            {c.status !== "Signed" && c.status !== "Draft" && (
              <button onClick={() => setSigning(c)} className="text-white rounded-full px-5 py-2 font-medium hover:opacity-90 active:scale-95" style={{ backgroundColor: color }} data-testid={`sign-contract-${c.id}`}>
                Sign this contract
              </button>
            )}
            {c.pdf_file_id && (
              <button onClick={() => viewPdf(c.id)} className="bg-slate-100 hover:bg-slate-200 rounded-full px-5 py-2 font-medium inline-flex items-center gap-1" data-testid={`portal-pdf-${c.id}`}>
                <Download className="w-4 h-4" /> Download signed PDF
              </button>
            )}
          </div>
        </div>
      ))}

      {signing && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setSigning(null)}>
          <div className="bg-white rounded-3xl p-8 max-w-lg w-full shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="sign-modal">
            <div className="flex items-start justify-between mb-6">
              <div>
                <p className="eyebrow mb-1">Type-to-sign</p>
                <h2 className="text-2xl font-bold tracking-tight">{signing.title}</h2>
              </div>
              <button onClick={() => setSigning(null)} className="p-1 hover:bg-slate-100 rounded-full"><X className="w-4 h-4" /></button>
            </div>
            <p className="text-sm text-slate-600 mb-4">By typing your full name below, you legally agree to this contract.</p>
            <input placeholder="Type your full name" value={signature} onChange={(e) => setSignature(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 mb-4 signature-cursive text-3xl text-slate-900" data-testid="signature-input" />
            <div className="bg-slate-50 rounded-xl p-4 mb-6">
              <p className="eyebrow mb-2">Preview</p>
              <p className="signature-cursive text-4xl text-slate-900 min-h-[3rem]">{signature || "—"}</p>
            </div>
            <button disabled={signature.length < 2} onClick={sign} className="w-full text-white rounded-full py-3 font-medium hover:opacity-90 active:scale-95 disabled:opacity-50" style={{ backgroundColor: color }} data-testid="confirm-sign-button">
              Sign & generate PDF
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function PortalBrand({ color }) {
  const [assets, setAssets] = useState(null);
  const [hex, setHex] = useState("");
  const [notes, setNotes] = useState("");
  const fileRef = useRef(null);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  const load = async () => {
    const { data } = await api.get("/portal/brand-assets");
    setAssets(data);
    setNotes(data?.typography_notes || "");
  };

  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/portal/brand-assets/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo uploaded");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const addHex = async () => {
    if (!/^#[0-9A-Fa-f]{6}$/.test(hex)) {
      toast.error("Enter valid hex (e.g. #FF5733)");
      return;
    }
    const next = [...(assets?.hex_codes || []), hex.toUpperCase()];
    await api.patch("/portal/brand-assets", { hex_codes: next });
    setHex("");
    await load();
  };

  const removeHex = async (i) => {
    const next = (assets?.hex_codes || []).filter((_, idx) => idx !== i);
    await api.patch("/portal/brand-assets", { hex_codes: next });
    await load();
  };

  const saveNotes = async () => {
    await api.patch("/portal/brand-assets", { typography_notes: notes });
    toast.success("Typography notes saved");
  };

  return (
    <div className="space-y-6 animate-fade-up" data-testid="portal-brand">
      <div className="bento p-8" data-testid="portal-logos-card">
        <p className="eyebrow mb-4">Logos</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          {(assets?.logo_urls || []).map((u, i) => (
            <div key={`${u}-${i}`} className="aspect-square rounded-2xl bg-slate-50 border border-slate-200 p-4 flex items-center justify-center">
              <img src={BACKEND + u} alt="logo" className="max-w-full max-h-full object-contain" />
            </div>
          ))}
          <button onClick={() => fileRef.current?.click()} className="aspect-square rounded-2xl border-2 border-dashed border-slate-200 hover:border-slate-400 hover:bg-slate-50 flex flex-col items-center justify-center gap-2 text-slate-500 group transition-all" data-testid="logo-upload-button">
            <Upload className="w-6 h-6 group-hover:scale-110 transition-transform" />
            <span className="text-xs font-medium">Upload logo</span>
          </button>
          <input ref={fileRef} type="file" accept=".png,.jpg,.jpeg,.svg,.webp" onChange={upload} className="hidden" data-testid="logo-file-input" />
        </div>
        <p className="text-xs text-slate-400">Accepted: PNG, JPG, SVG, WebP · Max 5MB</p>
      </div>

      <div className="bento p-8" data-testid="portal-colors-card">
        <p className="eyebrow mb-4">Brand colors</p>
        <div className="flex flex-wrap gap-3 mb-4">
          {(assets?.hex_codes || []).map((h, i) => (
            <div key={`${h}-${i}`} className="flex items-center gap-2 bg-slate-50 rounded-full pl-1 pr-2 py-1 border border-slate-200" data-testid={`hex-chip-${i}`}>
              <div className="w-8 h-8 rounded-full border-2 border-white shadow-md" style={{ backgroundColor: h }} />
              <code className="text-xs font-mono">{h}</code>
              <button onClick={() => removeHex(i)} className="text-slate-400 hover:text-red-500 p-0.5"><X className="w-3 h-3" /></button>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <input placeholder="#FF5733" value={hex} onChange={(e) => setHex(e.target.value)} className="flex-1 bg-slate-50 border border-slate-200 rounded-xl px-4 py-2 font-mono text-sm" data-testid="hex-input" />
          <button onClick={addHex} className="text-white rounded-full px-4 py-2 font-medium hover:opacity-90 active:scale-95 text-sm" style={{ backgroundColor: color }} data-testid="add-hex-button">Add color</button>
        </div>
      </div>

      <div className="bento p-8" data-testid="portal-typography-card">
        <p className="eyebrow mb-4">Typography notes</p>
        <textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Primary: Helvetica Neue. Headings bold. Body regular." className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 mb-4" data-testid="typography-notes-input" />
        <button onClick={saveNotes} className="text-white rounded-full px-5 py-2 font-medium hover:opacity-90 active:scale-95 text-sm" style={{ backgroundColor: color }} data-testid="save-typography-button">Save</button>
      </div>
    </div>
  );
}

function PortalRoadmap({ color, clientId }) {
  const [tasks, setTasks] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", description: "" });

  const load = async () => {
    const { data } = await api.get("/portal/roadmap");
    setTasks(data);
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    await api.post("/portal/roadmap/request", form);
    toast.success("Feature request submitted!");
    setForm({ title: "", description: "" });
    setShowForm(false);
    await load();
  };

  const approve = async (id) => {
    try {
      await api.post(`/portal/roadmap/${id}/approve`);
      toast.success("Approved");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const cols = [
    { key: "ToDo", label: "To do" },
    { key: "InProgress", label: "In progress" },
    { key: "Done", label: "Done" },
  ];

  return (
    <div className="animate-fade-up" data-testid="portal-roadmap">
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-slate-600">Read-only view. Submit feature requests or approve milestones.</p>
        <button onClick={() => setShowForm(true)} className="text-white rounded-full px-5 py-2 font-medium hover:opacity-90 active:scale-95 inline-flex items-center gap-2 text-sm" style={{ backgroundColor: color }} data-testid="request-feature-button">
          <Plus className="w-4 h-4" /> Request feature
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="portal-kanban">
        {cols.map((col) => (
          <div key={col.key} className="bg-white/60 rounded-2xl p-4 border border-slate-200 min-h-[400px]">
            <div className="flex items-center justify-between mb-4 px-2">
              <h3 className="font-bold text-sm uppercase tracking-wider">{col.label}</h3>
              <span className="text-xs text-slate-500 bg-white px-2 py-0.5 rounded-full border border-slate-200">{tasks.filter((t) => t.status === col.key).length}</span>
            </div>
            <div className="space-y-3">
              {tasks.filter((t) => t.status === col.key).map((t) => (
                <div key={t.id} className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm" data-testid={`portal-task-${t.id}`}>
                  <h4 className="font-semibold text-sm mb-1">{t.title}</h4>
                  {t.description && <p className="text-xs text-slate-500 mb-2">{t.description}</p>}
                  {t.client_approval_required && t.client_id === clientId && (
                    t.client_approved_at ? (
                      <span className="inline-flex items-center gap-1 text-xs bg-emerald-50 text-emerald-700 px-2 py-1 rounded-full"><Check className="w-3 h-3" /> Approved</span>
                    ) : (
                      <button onClick={() => approve(t.id)} className="inline-flex items-center gap-1 text-xs bg-amber-50 hover:bg-amber-100 text-amber-700 px-2 py-1 rounded-full" data-testid={`approve-${t.id}`}>
                        Approve milestone
                      </button>
                    )
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <form onSubmit={submit} className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="feature-request-modal">
            <div className="flex items-start justify-between mb-6">
              <h2 className="text-2xl font-bold tracking-tight">Request a feature</h2>
              <button type="button" onClick={() => setShowForm(false)} className="p-1 hover:bg-slate-100 rounded-full"><X className="w-4 h-4" /></button>
            </div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Title</label>
            <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 mb-4" data-testid="request-title-input" />
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Description</label>
            <textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 mb-6" data-testid="request-desc-input" />
            <button type="submit" className="w-full text-white rounded-full py-3 font-medium hover:opacity-90 active:scale-95" style={{ backgroundColor: color }} data-testid="submit-request-button">Submit request</button>
          </form>
        </div>
      )}
    </div>
  );
}
